<!-- @format -->
<template>
    <div class="activity-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="title-container">
                    <h3>{{ steps[step].title || 'Log Activity' }}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>

            <div class="content-wrapper">
                <div
                    v-if="step === 'addActivity' || step === 'updateActivity'"
                    class="activity-step"
                >
                    <div class="activity-form-row">
                        <div class="bordered-section-container">
                            <span class="bordered-section-header">Activity Details</span>
                            <div class="input-wrapper contact">
                                <label>Contact</label>
                                <contact-search
                                    v-model="activity.contact"
                                    :initialId="activity.contactId"
                                    :clientId="clientId"
                                    :disabled="isContactDropDownDisabled"
                                    :read-only="isContactDropDownDisabled"
                                    :filtering-contact-ids="filteringContactIds"
                                />
                                <div
                                    v-if="
                                        !isContactDropDownDisabled &&
                                        !$v.activity.contact.required &&
                                        hasSaveClicked
                                    "
                                    class="error"
                                >
                                    A contact must be selected
                                </div>
                            </div>
                            <div class="input-wrapper type">
                                <label>Activity Type</label>
                                <template v-if="!isTypeDropDownDisabled">
                                    <activity-types-dropdown
                                        v-model="activity.type"
                                        :disabled="isTypeDropDownDisabled"
                                        :customTypes="customTypes"
                                        placeholder="Select Activity..."
                                        :hide-custom-types="hideCustomTypes"
                                        @navigate="openNewActivityTypeWidget"
                                    />
                                    <div v-if="!$v.activity.type.required" class="error">
                                        A type must be selected
                                    </div>
                                </template>
                                <p v-else>{{ activity.type.value }}</p>
                                <div class="advice">
                                    To manage your custom activities go to your

                                    <button class="btn link-btn" @click="goToCustomLogActivities()">
                                        account settings.
                                    </button>
                                </div>
                            </div>
                            <div v-if="!hideOpportunityDropdown" class="input-wrapper contact">
                                <label>Is this activity related to an opportunity?</label>
                                <opportunity-search
                                    v-model="activity.opportunity"
                                    :client-id="clientId"
                                    :company-id="options.companyId"
                                />
                            </div>
                            <div v-show="isTypeMeeting" class="input-wrapper subject">
                                <label>Subject</label>
                                <app-input v-model="activity.subject" />
                            </div>
                            <div v-show="isTypeMeeting" class="input-wrapper location">
                                <label>Location</label>
                                <app-input v-model="activity.location" />
                            </div>
                            <div v-show="isTypeMeeting" class="input-wrapper status">
                                <label>Status</label>
                                <meeting-status-dropdown v-model="activity.status" />
                            </div>
                            <div v-show="isTypeMeeting" class="input-wrapper activity-participants">
                                <label>Other Participants</label>
                                <activity-participant-search
                                    :clientId="clientId"
                                    :initialId="userId"
                                    @input="addParticipant($event)"
                                />
                                <div class="other-participants">
                                    <div
                                        v-for="participant in activity.participants"
                                        :key="participant.id"
                                        class="participant solid-grey-bg"
                                    >
                                        <app-profile-image :src="participant.profileImageUrl" />
                                        {{ participant.name }}
                                        <s3-close-svg
                                            @click.native="removeParticipant(participant)"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div v-show="isTypeMeeting" class="input-wrapper duration">
                                <label>Start Date</label>
                                <div class="wrapper start-date">
                                    <div class="date-time">
                                        <app-date-picker v-model="activity.startDate.date" />
                                        <time-picker v-model="activity.startDate.time" />
                                    </div>
                                </div>
                                <label>End Date</label>
                                <div class="wrapper end-date">
                                    <div class="date-time">
                                        <app-date-picker v-model="activity.endDate.date" />
                                        <time-picker v-model="activity.endDate.time" />
                                    </div>
                                    <div v-if="startDateTime > endDateTime" class="error">
                                        Your end date must be after your start date
                                    </div>
                                </div>
                            </div>

                            <div
                                class="input-wrapper description"
                                v-if="activity.type.value !== 'Select Activity...'"
                            >
                                <label>Description</label>
                                <froala v-model="activity.description" :config="config" />
                                <div
                                    v-if="!$v.activity.description.required && hasSaveClicked"
                                    class="error"
                                >
                                    A description must be added
                                </div>
                            </div>

                            <div
                                v-if="activity.contact && isTypeMeeting"
                                class="input-container registered-meetings"
                            >
                                <activity-registered-meetings
                                    ref="activityRegisteredMeetings"
                                    :clientId="clientId"
                                    :registeredMeetings="activity.registeredMeetings"
                                    :activityId="activity.id"
                                    @activityIdNeeded="postForMeetings"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-else-if="step === 'saving' || step === 'loading'"
                    class="activity-step saving-step"
                >
                    <quick-add-saving-svg />
                </div>
                <div v-else-if="step === 'creatingNewType'" class="activity-step">
                    <activity-type-form
                        :value="customType"
                        @created="handleCreatedType"
                        @reset-form="handleReset"
                    ></activity-type-form>
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel</app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="steps[step].buttonText === 'Add Custom Activity Type'"
                        class="transparent-btn"
                        @click="goBack"
                    >
                        <span class="fa fa-arrow-left"></span> Go Back
                    </app-button>
                    <app-button :loading="steps[step].loading" @click="steps[step].action">
                        {{ steps[step].buttonText }}
                        <template v-if="steps[step].buttonText === 'Add Custom Activity Type'">
                            <plus-icon-svg colorClass="white" />
                        </template>
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ActivitiesService } from '@/scripts/services/activities/activities-service';

const ActivityTypeForm = () => import('@/components/sliding-widgets/activity-type-form');
const NewActivityTypeForm = () => import('@/components/sliding-widgets/new-activity-type-form');
const ActivityTypesDropdown = () => import('@/components/activity/activity-types-dropdown');
const ActivityRegisteredMeetings = () =>
    import('@/components/activity/activity-registered-meetings');
const MeetingStatusDropdown = () => import('@/components/activity/meeting-status-dropdown');
const AppButton = () => import('@/components/common/app-button');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const AppInput = () => import('@/components/common/app-input');
const AppDatePicker = () => import('@/components/common/app-date-picker');
const ContactSearch = () => import('@/components/contact/contact-search');
const OpportunitySearch = () => import('@/components/opportunity/opportunity-search');
const ActivityParticipantSearch = () => import('@/components/activity/activity-participant-search');
const QuickAddSavingSvg = () => import('@/components/svg/quick-add/quick-add-saving-svg');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const TimePicker = () => import('@/components/common/time-picker');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const PlusIconSvg = () => import('@/components/svg/plus-icon-svg');

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import { LogLevel } from '@/scripts/logger';
import { CustomActivityTypeService } from '@/scripts/services/custom-activity/custom-type-service';
import { cloneDeep } from 'lodash';

export default {
    name: 'activity-widget',

    components: {
        'activity-types-dropdown': ActivityTypesDropdown,
        'activity-registered-meetings': ActivityRegisteredMeetings,
        'meeting-status-dropdown': MeetingStatusDropdown,
        'app-button': AppButton,
        'app-input': AppInput,
        'app-profile-image': AppProfileImage,
        'app-date-picker': AppDatePicker,
        'contact-search': ContactSearch,
        'opportunity-search': OpportunitySearch,
        'activity-participant-search': ActivityParticipantSearch,
        'quick-add-saving-svg': QuickAddSavingSvg,
        's3-close-svg': S3CloseSvg,
        'time-picker': TimePicker,
        'widget-close-svg': WidgetCloseSvg,
        'activity-type-form': ActivityTypeForm,
        'new-activity-type-form': NewActivityTypeForm,
        'plus-icon-svg': PlusIconSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            isLoading: false,
            hasDescriptionError: false,
            isTypeDropDownDisabled: this.options.isTypeDropDownDisabled || false,
            isContactDropDownDisabled: this.options.isContactDropDownDisabled || false,
            clientId: this.options.clientId,
            filteringContactIds: this.options.contactIds,
            presetDealId: this.options.dealId,
            hideCustomTypes: this.options.hideCustomTypes || false,
            hasSaveClicked: false,

            customTypes: [],
            activity: {
                id: this.options.activityId,
                cached: this.options.cached,
                description: this.options.description,
                type: {
                    id:
                        (this.options.type && this.options.type.id) ||
                        (this.options.customActivityType && this.options.customActivityType.id) ||
                        null,
                    value:
                        this.options.type && this.options.type.value
                            ? this.options.customActivityType
                                ? this.options.customActivityType.name
                                : this.options.type.value
                            : 'Select Activity...',
                },
                contactId: this.options.contactId,
                contact: null,
                opportunity: null,
                subject: '',
                location: '',
                status: 'Tentative',
                duration: 0,
                startDate: {
                    date: null,
                    time: null,
                },

                endDate: {
                    date: null,
                    time: null,
                },

                participants: [],
                registeredMeetings: [],
            },

            customType: {
                name: '',
                icon: '',
                iconColor: {
                    value: '#F30070',
                },
                accountId: '',
            },

            step: 'addActivity',
            steps: {
                loading: {
                    title: 'Loading',
                    buttonText: 'Loading',
                    action: () => {},
                    loading: true,
                },

                addActivity: {
                    title: 'Log Activity',
                    buttonText: 'Add Activity',
                    action: this.handleClickLogActivity,
                    loading: false,
                },

                updateActivity: {
                    title: 'Update Activity',
                    buttonText: 'Save Activity',
                    action: this.handleClickSaveActivity,
                    loading: false,
                },

                saving: {
                    title: 'Saving Activity',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },

                creatingNewType: {
                    title: 'Add New Activity Type',
                    buttonText: 'Add Custom Activity Type',
                    action: () => {
                        EventBus.$emit('custom-activity:submit');
                    },
                    loading: false,
                },
            },
        };
    },

    validations: {
        activity: {
            contact: {
                required,
            },

            type: {
                required,
            },

            description: {
                required,
            },
        },
    },

    computed: {
        isNewOpportunity() {
            return !this.presetDealId;
        },

        userId() {
            return this.$store.state.user.id;
        },

        isTypeValid() {
            return ['Call', 'Email', 'Note', 'Meeting'].includes(this.activity.type);
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        isCoreType() {
            return ActivitiesService.isActivityCoreType(this.activity);
        },

        isTypeMeeting() {
            return ActivitiesService.isActivityMeetingType(this.activity);
        },

        isTypeCall() {
            return this.activity.type.value === 'Call';
        },

        isTypeEmail() {
            return this.activity.type.value === 'Email';
        },

        isTypeNote() {
            return this.activity.type.value === 'Note';
        },

        config() {
            return {
                fileUpload: true,
                key: this.$helpers.getFroalaKey(),
                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 50 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],
                requestHeaders: {
                    Authorization: 'Bearer ' + localStorage.jwt,
                },

                placeholderText: 'Type Away...',
                toolbarButtons: ['bold', 'italic', 'insertLink', 'align', 'insertFile'],
            };
        },

        startDateTime() {
            var date = this.$moment(this.activity.startDate.date).utc().format('YYYY-MM-DD');

            return this.$moment(date + this.activity.startDate.time, 'YYYY-MM-DDHH:mma');
        },

        endDateTime() {
            var date = this.$moment(this.activity.endDate.date).utc().format('YYYY-MM-DD');

            return this.$moment(date + this.activity.endDate.time, 'YYYY-MM-DDHH:mma');
        },

        meetingDuration() {
            var duration = this.endDateTime.diff(this.startDateTime, 'seconds');

            return duration;
        },

        isValid() {
            this.$v.$touch();

            return this.$v.$invalid;
        },

        hideOpportunityDropdown() {
            return this.options.hideOpportunityDropdown || !!this.activity.type.id || false;
        },
    },

    watch: {
        'activity.type'() {
            this.steps.addActivity.title = this.activity.type.value;
        },
    },

    created() {
        this.setFroalaDefaults();
        if (this.activity.id || this.activity.cached || this.options.customActivityType) {
            this.step = 'updateActivity';
            this.isTypeDropDownDisabled = true;
            this.isContactDropDownDisabled = true;
            if (!this.activity.cached) {
                this.getActivity();
            } else {
                const activityRef = this.options.activityRef;
                this.activity.startDate = {
                    date: new Date(activityRef.startDate.date),
                    time: activityRef.startDate.time,
                };
                this.activity.endDate = {
                    date: new Date(activityRef.endDate.date),
                    time: activityRef.endDate.time,
                };
                this.activity.location = activityRef.location;
                this.activity.subject = activityRef.subject;
                this.activity.status = activityRef.status;
                this.activity.participants = cloneDeep(activityRef.participants);
            }
        } else {
            this.activity.startDate.date = new Date();
            this.activity.endDate.date = new Date();
            this.activity.startDate.time = this.$moment().format('HH:mm:ss');
            this.activity.endDate.time = this.$moment().add(30, 'm').format('HH:mm:ss');
        }

        EventBus.$on('registered-meeting:update', this.updateRegisteredMeetings);
    },

    beforeDestroy() {
        EventBus.$off('registered-meeting:update', this.updateRegisteredMeetings);
    },

    methods: {
        handleClickLogActivity() {
            this.postActivity();
        },

        handleClickSaveActivity() {
            this.patchActivity();
        },

        openNewActivityTypeWidget() {
            this.step = 'creatingNewType';
        },

        setFroalaDefaults() {
            $.FroalaEditor.DefineIcon('insertFile', {
                template: 'image',
                SRC: 'https://cdn.honeycrm.com/files/attach-icon-white.svg',
                ALT: 'Upload File',
            });
        },

        postForMeetings() {
            if (this.isValid) {
                return;
            }

            this.step = 'loading';

            var activity = {
                SalesNote: this.activity.description,
            };

            if (this.presetDealId || this.activity.opportunity) {
                activity.DealId = this.presetDealId || this.activity.opportunity.id;
            }

            ActivitiesService.addMeetingDetails(this.activity, activity);

            this.$api
                .post('/api/MeetingActivities/', activity)
                .then((response) => {
                    this.activity.id = response.data.Id;
                    EventBus.$emit('opportunity:update');
                    EventBus.$emit('activity:update');
                    this.step = 'updateActivity';
                    this.$nextTick(() => {
                        this.$refs.activityRegisteredMeetings.add();
                    });
                    this.$helpers.showSuccessNotification('Your activity was successfully added.');
                })
                .catch((error) => {
                    this.step = 'addActivity';
                    this.$helpers.showFailNotification(
                        'An error occurred while adding your activity.',
                        { error },
                    );
                });
        },

        updateRegisteredMeetings() {
            this.$api
                .get(
                    `/service/RegisteredMeetings?$filter=MeetingActivityId eq ${this.activity.id}&$expand=Agency($select=Id,Name,LogoImageUrl)`,
                )
                .then((response) => {
                    this.activity.registeredMeetings = response.data.value.map((meeting) => {
                        return {
                            id: meeting.Id,
                            meetingActivityId: meeting.MeetingActivityId,
                            agency: {
                                id: meeting.Agency.Id,
                                name: meeting.Agency.Name,
                                logoImageUrl:
                                    meeting.Agency.LogoImageUrl ||
                                    require('@/assets/images/company-avatar.png'),
                            },
                        };
                    });
                });
        },

        addParticipant(newParticipant) {
            var existsInParticipants = this.activity.participants.some((participant) => {
                return participant.id === newParticipant.id;
            });

            var existsInContact =
                this.activity.contact && this.activity.contact.id === newParticipant.id;

            if (!existsInParticipants && !existsInContact) {
                this.activity.participants.push(newParticipant);
            }
        },

        removeParticipant(participant) {
            this.activity.participants.splice(this.activity.participants.indexOf(participant), 1);
        },

        getSelectParam() {
            if (this.isTypeMeeting) {
                return '$select=Id,ActivityDate,TriDigital.EntityDomain.Entities.MeetingActivity/MeetingSubject,TriDigital.EntityDomain.Entities.MeetingActivity/MeetingLocation,TriDigital.EntityDomain.Entities.MeetingActivity/MeetingDuration,TriDigital.EntityDomain.Entities.MeetingActivity/MeetingStatus,TriDigital.EntityDomain.Entities.MeetingActivity/SalesNote,TriDigital.EntityDomain.Entities.MeetingActivity/UserId,TriDigital.EntityDomain.Entities.MeetingActivity/ContactId,TriDigital.EntityDomain.Entities.MeetingActivity/Contact,TriDigital.EntityDomain.Entities.MeetingActivity/User';
            } else {
                return '$select=Id,ActivityDate,TriDigital.EntityDomain.Entities.SalesActivity/SalesNote';
            }
        },

        getExpandParam() {
            return '$expand=ActivityParticipants($expand=Contact($select=Id,FirstName,LastName,ProfileImageUrl),User($select=Id,FirstName,LastName,ProfileImageUrl)),TriDigital.EntityDomain.Entities.MeetingActivity/Contact($select=Id,FirstName,LastName,ProfileImageUrl),TriDigital.EntityDomain.Entities.MeetingActivity/User($select=Id,FirstName,LastName,ProfileImageUrl),TriDigital.EntityDomain.Entities.MeetingActivity/RegisteredMeetings($select=Id,AgencyId,MeetingActivityId;$expand=Agency($select=Id,Name,LogoImageUrl)),Deal($select=Id,Name)';
        },

        getFilterParam() {
            var filterParam = '';

            var filters = this.getFilters();

            if (filters.length > 0) {
                filterParam = `$filter=${filters.join(' and ')}`;
            }

            return filterParam;
        },

        getFilters() {
            var queryFilters = [];

            if (this.activity.id) {
                queryFilters.push(`Id eq ${this.activity.id}`);
            }

            return queryFilters;
        },

        getParticipants(activityResponse) {
            var participants = [];

            if (activityResponse.ActivityParticipants.length > 0) {
                activityResponse.ActivityParticipants.map((participant) => {
                    if (participant.Contact && participant.Contact.Id !== this.activity.contactId) {
                        participants.push({
                            id: participant.Contact.Id,
                            name: this.$helpers.getName(
                                participant.Contact.FirstName,
                                participant.Contact.LastName,
                            ),
                            profileImageUrl:
                                participant.Contact.ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                            type: 'Contact',
                        });
                    } else if (participant.User) {
                        participants.push({
                            id: participant.User.Id,
                            name: this.$helpers.getName(
                                participant.User.FirstName,
                                participant.User.LastName,
                            ),
                            profileImageUrl:
                                participant.User.ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                            type: 'User',
                        });
                    }
                });
            }

            if (activityResponse.UserId) {
                participants.push({
                    id: activityResponse.User.Id,
                    name: this.$helpers.getName(
                        activityResponse.User.FirstName,
                        activityResponse.User.LastName,
                    ),
                    profileImageUrl:
                        activityResponse.User.ProfileImageUrl ||
                        require('@/assets/images/avatar.png'),
                    type: 'User',
                });
            }

            if (
                activityResponse.Contact &&
                activityResponse.Contact.Id !== this.activity.contactId
            ) {
                participants.push({
                    id: activityResponse.Contact.Id,
                    name: this.$helpers.getName(
                        activityResponse.Contact.FirstName,
                        activityResponse.Contact.LastName,
                    ),
                    profileImageUrl:
                        activityResponse.Contact.ProfileImageUrl ||
                        require('@/assets/images/avatar.png'),
                    type: 'Contact',
                });
            }

            return participants;
        },

        getActivity() {
            this.isLoading = true;

            var restUrl = `/service/Activities?clientId=${
                this.clientId
            }&${this.getFilterParam()}&${this.getExpandParam()}&${this.getSelectParam()}`;
            if (this.options.customActivityType) {
                restUrl = `/service/CustomActivities?$filter=Id eq ${this.options.activityId}`;
            }

            this.$api
                .get(restUrl)
                .then((response) => {
                    var activityResponse = response.data.value[0];

                    this.activity.id = this.options.activityId;
                    this.activity.description =
                        activityResponse.SalesNote || activityResponse.Description;

                    if (activityResponse.Deal) {
                        const { Id, Name } = activityResponse.Deal;
                        this.activity.opportunity = { id: Id, name: Name };
                    }

                    if (this.isTypeMeeting) {
                        this.activity.subject = activityResponse.MeetingSubject;
                        this.activity.location = activityResponse.MeetingLocation;
                        this.activity.status = activityResponse.MeetingStatus;
                        this.activity.duration = activityResponse.MeetingDuration;
                        this.activity.participants = this.getParticipants(activityResponse);

                        if (activityResponse.ActivityDate) {
                            var startDateTime = this.$moment
                                .utc(activityResponse.ActivityDate)
                                .local();
                            var endDateTime = this.$moment(startDateTime).add(
                                activityResponse.MeetingDuration,
                                's',
                            );

                            this.activity.startDate.date = startDateTime.toDate();
                            this.activity.startDate.time = startDateTime.format('HH:mm:ssa');

                            this.activity.endDate.date = endDateTime.toDate();
                            this.activity.endDate.time = endDateTime.format('HH:mm:ssa');
                        }

                        if (activityResponse.RegisteredMeetings.length > 0) {
                            this.activity.registeredMeetings =
                                activityResponse.RegisteredMeetings.map((registeredMeeting) => {
                                    return {
                                        id: registeredMeeting.Id,
                                        meetingActivityId: registeredMeeting.MeetingActivityId,
                                        agency: {
                                            id: registeredMeeting.Agency.Id,
                                            name: registeredMeeting.Agency.Name,
                                            logoImageUrl:
                                                registeredMeeting.Agency.LogoImageUrl ||
                                                require('@/assets/images/company-avatar.png'),
                                        },
                                    };
                                });
                        }
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(`Failed to get activity information.`, {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        addMeetingDetails(activity) {
            var activityDate = `${this.$moment(this.activity.startDate.date).format(
                'YYYY-MM-DD',
            )}T${this.$moment(this.activity.startDate.time, 'HH:mma').utc().format('HH:mm:ss')}Z`;

            activity.ContactId = null;
            activity.UserId = null;
            activity.ActivityDate = activityDate;
            activity.MeetingSubject = this.activity.subject;
            activity.MeetingLocation = this.activity.location;
            activity.MeetingStatus = this.activity.status;
            activity.MeetingDuration = this.meetingDuration;
            activity.Participants = this.activity.participants.map((participant) => {
                if (participant.type === 'User') {
                    return {
                        ContactId: null,
                        UserId: participant.id,
                    };
                } else if (participant.type === 'Contact') {
                    return {
                        ContactId: participant.id,
                        UserId: null,
                    };
                }
            });

            if (this.activity.contact) {
                activity.Participants.push({
                    ContactId: this.activity.contact.id,
                });
            }
        },

        postActivity() {
            this.hasSaveClicked = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            if (this.activity.contact) {
                this.step = 'saving';

                ActivitiesService.postActivity(this.activity, { presetDealId: this.presetDealId })
                    .then(() => {
                        EventBus.$emit('activity:update');

                        if (this.isCoreType) {
                            EventBus.$emit('opportunity:update');

                            this.$helpers.showSuccessNotification(
                                `A ${this.activity.type.value.toLowerCase()} ${
                                    this.activity.contact
                                        ? 'with ' + this.activity.contact.name
                                        : ''
                                } was just logged.`,
                            );
                        } else {
                            this.$helpers.showSuccessNotification(
                                `A ${this.activity.type.value.toLowerCase()} ${
                                    this.activity.contact
                                        ? 'with ' + this.activity.contact.name
                                        : ''
                                } was just logged.`,
                            );
                        }

                        this.closeWidget();
                    })
                    .catch((error) => {
                        if (this.isCoreType) {
                            this.$helpers.showFailNotification(
                                'An error occurred while adding your activity.',
                                { error },
                            );
                        } else {
                            this.$helpers.showFailNotification(
                                'An error occurred while logging your custom activity.',
                                { error },
                            );
                        }
                    });
            } else {
                this.$helpers.showFailNotification('No contact is selected.', {
                    level: LogLevel.INFO,
                });
            }
        },

        patchActivity() {
            this.isLoading = true;

            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.step = 'saving';

            ActivitiesService.patchActivity(this.activity)
                .then(() => {
                    EventBus.$emit('activity:update', this.activity);
                    if (this.isCoreType) {
                        EventBus.$emit('opportunity:update');
                    }

                    this.closeWidget();
                })
                .catch((error) => {
                    console.error(error);
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your activity.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goBack() {
            this.step = 'addActivity';
            this.activity.type = {
                id: null,
                value: 'Select Activity...',
            };
            this.customType = this.initalCustomTypeState();
        },

        initalCustomTypeState() {
            return {
                name: '',
                icon: '',
                iconColor: {
                    value: '#F30070',
                },
                accountId: '',
            };
        },

        goToCustomLogActivities() {
            this.$router.push(`${this.routeBaseUrl}/Account/Customization/CustomActivityTypes`);
            this.closeWidget();
        },

        handleCreatedType(body) {
            this.step = 'addActivity';
            this.customType = this.initalCustomTypeState();
            var notification = {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: `${body.Name} successfully added!`,
                actionLabel: 'View',
                action: () => {
                    this.$router.push(
                        `${this.routeBaseUrl()}/Account/Customization/CustomActivityType`,
                    );
                    EventBus.$emit('close-notification', this.notification);
                },
            };

            this.activity.type = body.Name;
            EventBus.$emit('show-notification', notification);
        },

        handleReset() {
            this.loading = false;
        },
    },
};
</script>

<style>
.activity-widget .bordered-section-container {
    border: 1px solid #cac6cb;
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 32px;
}

.activity-widget .bordered-section-container:first-child {
    margin-top: 12px;
}

.activity-widget .bordered-section-container::before {
    content: '';
    position: absolute;
    top: -1px;
    height: 1px;
    background: #fff;
}

.activity-widget .bordered-section-container::before {
    right: 0;
    left: 0;
    margin: 0 auto;
    width: 36%;
}

.activity-widget .bordered-section-container span.bordered-section-header {
    position: absolute;
    right: 0;
    left: 0;
    top: 0;
    margin: 0 auto;
    transform: translateY(-50%);
    text-align: center;
    font: normal normal 500 18px/11px 'Roboto';
    letter-spacing: 0px;
    color: #382153;
}

.activity-widget .loading-screen {
    margin: 0 auto;
}

.activity-widget .form-group--error .error {
    font-size: 12px;
    color: #c00;
    margin-top: 2px;
}

.activity-widget .advice {
    margin-top: 8px;
    text-align: left;
    font: normal normal normal 12px/14px Roboto;
    letter-spacing: 0px;
    opacity: 1;
    display: inline-block;
}

.activity-widget .advice .link-btn {
    font: normal normal normal 12px/14px Roboto;
}

.activity-widget .advice .btn {
    min-width: auto;
}

.activity-widget .activity-step {
    width: 100%;
}

.activity-widget .activity-step .activity-form-row {
    display: flex;
    flex-direction: column;
}

.activity-widget .activity-step .activity-form-row.activity-profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.activity-widget .activity-step .activity-form-row .input-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 12px;
}

.activity-widget .activity-step .activity-form-row .input-wrapper:last-child {
    margin-bottom: 0;
}

.activity-widget .wrapper.start-date,
.activity-widget .wrapper.end-date {
    margin-bottom: 12px;
}

.activity-widget .wrapper.end-date {
    display: flex;
    flex-direction: column;
}

.activity-widget .wrapper .date-time {
    width: 100%;
    display: flex;
}

.activity-widget .activity-step .activity-form-row .input-wrapper .error {
    color: #f30070;
}

.activity-widget .activity-step.scrub-step .activity-form-row {
    flex-direction: column;
}

.activity-widget .activity-step.saving-step svg {
    width: 100%;
}

.activity-widget .action-container {
    display: flex;
}

.activity-widget .action-container .plus-icon-svg {
    margin-left: 8px;
}

.activity-widget .action-container .btn {
    margin-left: 15px;
}

.activity-widget .action-container .btn.transparent-btn:hover {
    background-color: #18aeff;
    color: #fff;
}

.activity-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #382153;
    border: 1px solid #877d88;
}

.activity-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.activity-widget .action-container .btn.transparent-btn .fa.fa-arrow-left {
    margin-right: 16px;
}

.activity-widget .action-container .btn.transparent-btn {
    color: #18aeff;
    transition: 0.3s ease-out;
    border: 1px solid #18aeff;
    background: transparent;
}

.activity-widget .action-container .btn.transparent-btn:hover {
    transition: 0.2s ease-in-out;
}

.activity-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.activity-widget .input-wrapper.duration .wrapper {
    display: flex;
}

.activity-widget .input-wrapper.duration .wrapper .time-picker {
    align-items: center;
}

.activity-widget .input-wrapper.duration .app-input .input-container {
    margin-bottom: 0;
}

.activity-widget .input-wrapper.duration .app-input.duration-input {
    margin-right: 15px;
}

.activity-widget .wrapper .app-date-picker {
    width: 400px;
}

.activity-widget .wrapper .time-picker {
    width: 400px;
}

.activity-widget .time-picker .colon {
    padding-top: 6px;
}

.activity-widget .time-picker .meridian {
    width: 100px;
}

.activity-widget .app-date-picker .vdp-datepicker__calendar {
    left: 0;
    bottom: 40px;
    z-index: 10000;
}

.activity-widget .other-participants {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.activity-widget .other-participants .participant {
    color: #ffffff;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.activity-widget .other-participants .participant svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.activity-widget .other-participants .participant .s3-close-svg .a {
    stroke: #ffffff;
}

.activity-widget .activity-participant-search {
    margin-bottom: 10px;
}

.activity-widget .app-profile-image .background-image {
    height: 15px;
    width: 15px;
    margin-right: 8px;
}

.activity-widget .time-picker .hour-input,
.activity-widget .time-picker .minute-input {
    width: 75px;
}

.activity-widget .activity-step .activity-form-row .input-wrapper.description {
    margin-bottom: 24px;
}

.activity-widget .activity-step .activity-form-row .label {
    display: block;
    font-size: 16px;
    font-family: 'Roboto';
    font-weight: 400;
    margin-bottom: 7px;
}

.activity-widget .fr-toolbar .fr-command.fr-btn img {
    margin: 11px;
    width: 17px;
}

.activity-widget a.type-anchor {
    color: #1fa2ff;
}

.activity-widget a.type-anchor:hover {
    text-decoration: none;
    cursor: pointer;
}

.activity-widget .app-multiselect-new-wrapper .multiselect--disabled,
.activity-widget .app-multiselect-new-wrapper .multiselect--disabled .multiselect__select {
    background: unset;
}

.activity-widget .app-multiselect-new-wrapper .multiselect--disabled .multiselect__tags {
    background: #ededed;
}
</style>
